import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { ColorsService } from '@services/colors/colors.service';
import { MaterialColor } from '@services/colors/interfaces/colors.interfaces';
import { ModalService } from '@services/components/ModalService/modal.service';
import { ToastService } from '@services/components/ToastService/toast.service';
import {
  GenericConfirmModalComponent,
  GenericConfirmModalData,
} from '@shared/components/UI/generic-confirm-modal/generic-confirm-modal.component';
import { GenericErrorModalComponent } from '@shared/components/UI/generic-error-modal/generic-error-modal.component';
import {
  buildSuccessColorToast,
  SUCCESS_DELETE_COLOR_TOAST,
} from '@shared/constants/toasts.constants';

import {
  ColorConflictModalComponent,
  ColorConflictModalData,
} from './color-conflict-modal/color-conflict-modal.component';
import {
  ColorDeleteBlockedModalComponent,
  ColorDeleteBlockedModalData,
} from './color-delete-blocked-modal/color-delete-blocked-modal.component';

import { COLOR_IN_USE_ERROR_CODE } from '@constants/codes.constants';
import {
  COLOR_CONFLICT_MODAL_CONFIG,
  DELETE_COLOR_BLOCKED_MODAL_CONFIG,
  DELETE_COLOR_CONFIRM_MODAL_CONFIG,
  GENERIC_SERVICE_ERROR_MODAL,
  LOAD_ERROR_MODAL_CONFIG,
} from '@constants/modal.constants';

@Component({
  selector: 'app-manage-colors',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './manage-colors.html',
  styleUrl: './manage-colors.css',
})

export class ManageColorsComponent implements OnInit {
  public viewMode = signal<'list' | 'form'>('list');
  public colors = signal<MaterialColor[]>([]);
  public isLoading = signal(false);
  public isSubmitting = signal(false);

  public colorForm!: FormGroup;
  public editingColorId: string | null = null;

  private originalFormValues: Record<string, unknown> | null = null;
  private pendingFormValues: Record<string, unknown> | null = null;

  private colorsService = inject(ColorsService);
  private toastService = inject(ToastService);
  private modalService = inject(ModalService);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.initForm();
    this.loadColors();
  }

  public get hasFormChanged(): boolean {
    if (!this.editingColorId || !this.originalFormValues) {
      return true;
    }
    return (
      JSON.stringify(this.colorForm.value) !==
      JSON.stringify(this.originalFormValues)
    );
  }

  public get hexCodesArray(): FormArray {
    return this.colorForm.get('hexCodes') as FormArray;
  }

  private initForm(): void {
    this.colorForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      internalName: [''],
      hexCodes: this.fb.array([this.createHexControl('#000000')]),
      isAvailable: [true],
    });
  }

  private createHexControl(value: string): FormControl {
    return this.fb.control(value, [
      Validators.pattern(/^#([0-9A-Fa-f]{6})$/),
      Validators.required,
    ]);
  }

  public addHexCode(): void {
    if (this.hexCodesArray.length < 3) {
      this.hexCodesArray.push(this.createHexControl('#000000'));
    }
  }

  public removeHexCode(index: number): void {
    if (this.hexCodesArray.length > 1) {
      this.hexCodesArray.removeAt(index);
    }
  }

  public openNewForm(): void {
    this.editingColorId = null;
    this.colorForm.reset({
      name: '',
      internalName: '',
      isAvailable: true,
    });

    this.hexCodesArray.clear();
    this.hexCodesArray.push(this.createHexControl('#000000'));

    this.viewMode.set('form');
  }

  public openEditForm(color: MaterialColor): void {
    this.editingColorId = color.id;

    const hexArray = [
      color.primaryHex,
      color.secondaryHex,
      color.tertiaryHex,
    ].filter(Boolean) as string[];

    if (!hexArray.length) {
      hexArray.push('#000000');
    }

    this.hexCodesArray.clear();
    hexArray.forEach((hex) =>
      this.hexCodesArray.push(this.createHexControl(hex.trim())),
    );

    const values = {
      name: color.name,
      internalName: color.label || '',
      hexCodes: hexArray,
      isAvailable: color.isAvailable,
    };

    this.colorForm.patchValue(values);
    this.originalFormValues = { ...values };
    this.viewMode.set('form');
  }

  public closeForm(): void {
    this.viewMode.set('list');
    this.colorForm.reset();
    this.originalFormValues = null;
  }

  public onSubmit(): void {
    if (this.colorForm.invalid) {
      this.colorForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    const raw = this.colorForm.value;
    const hexCodes = raw.hexCodes as string[];

    const formValues = {
      name: raw.name,
      label: raw.internalName || null,
      primaryHex: hexCodes[0] || null,
      secondaryHex: hexCodes[1] || null,
      tertiaryHex: hexCodes[2] || null,
      isAvailable: raw.isAvailable,
    };

    const request$ = this.editingColorId
      ? this.colorsService.updateColor(this.editingColorId, formValues)
      : this.colorsService.createColor(formValues);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.open(
          buildSuccessColorToast(!!this.editingColorId),
        );
        this.isSubmitting.set(false);
        this.loadColors();
        this.closeForm();
      },
      error: (err) => {
        this.isSubmitting.set(false);

        if (
          err.error?.code === COLOR_IN_USE_ERROR_CODE &&
          Array.isArray(err.error?.products)
        ) {
          this.pendingFormValues = formValues;
          this.openConflictModal(err.error.products);
        } else {
          this.modalService.openModal(GENERIC_SERVICE_ERROR_MODAL);
        }
      },
    });
  }

  public deleteColor(color: MaterialColor): void {
    const data: GenericConfirmModalData = {
      message: `¿Estás seguro de eliminar el color "${color.name}"?`,
      primaryButtonLabel: 'Eliminar',
    };

    this.modalService
      .openModal<boolean, GenericConfirmModalData>({
        ...DELETE_COLOR_CONFIRM_MODAL_CONFIG,
        component: GenericConfirmModalComponent,
        data,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ reason }) => {
        if (reason !== 'confirm') {
          return;
        }

        this.colorsService
          .deleteColor(color.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.toastService.open(SUCCESS_DELETE_COLOR_TOAST);
              this.loadColors();
            },
            error: (err) => {
              if (
                err.error?.code === COLOR_IN_USE_ERROR_CODE &&
                Array.isArray(err.error?.products)
              ) {
                this.modalService.openModal({
                  ...DELETE_COLOR_BLOCKED_MODAL_CONFIG,
                  component: ColorDeleteBlockedModalComponent,
                  data: {
                    products: err.error.products,
                  } as ColorDeleteBlockedModalData,
                });
              } else {
                this.modalService.openModal(GENERIC_SERVICE_ERROR_MODAL);
              }
            },
          });
      });
  }

  private openConflictModal(products: string[]): void {
    const data: ColorConflictModalData = { products };

    this.modalService
      .openModal<boolean, ColorConflictModalData>({
        ...COLOR_CONFLICT_MODAL_CONFIG,
        component: ColorConflictModalComponent,
        data,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ reason }) => {
        if (reason !== 'confirm') {
          this.pendingFormValues = null;
          return;
        }

        if (!this.editingColorId || !this.pendingFormValues) {
          return;
        }

        this.isSubmitting.set(true);

        this.colorsService
          .updateColor(
            this.editingColorId,
            this.pendingFormValues,
            true,
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.toastService.open(buildSuccessColorToast(true));
              this.isSubmitting.set(false);
              this.pendingFormValues = null;
              this.loadColors();
              this.closeForm();
            },
            error: () => {
              this.modalService.openModal(GENERIC_SERVICE_ERROR_MODAL);
              this.isSubmitting.set(false);
            },
          });
      });
  }

  private loadColors(): void {
    this.isLoading.set(true);

    this.colorsService
      .getColorsAdmin()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.colors.set(data);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);

          this.modalService
            .openModal<void>({
              ...LOAD_ERROR_MODAL_CONFIG,
              component: GenericErrorModalComponent,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ reason }) => {
              if (reason === 'confirm') {
                this.loadColors();
              }
            });
        },
      });
  }

  public getSwatchStyle(color: MaterialColor): {
    [klass: string]: any;
  } {
    const hexes = [
      color.primaryHex,
      color.secondaryHex,
      color.tertiaryHex,
    ].filter(Boolean);

    if (!hexes.length) {
      return { backgroundColor: '#fff' };
    }

    if (color.colorType === 'mono' || hexes.length === 1) {
      return { backgroundColor: hexes[0] };
    }

    if (color.colorType === 'bi' || hexes.length === 2) {
      return {
        background: `linear-gradient(135deg, ${hexes[0]} 50%, ${hexes[1]} 50%)`,
      };
    }

    return {
      background: `linear-gradient(135deg,
        ${hexes[0]} 33.33%,
        ${hexes[1]} 33.33%,
        ${hexes[1]} 66.66%,
        ${hexes[2]} 66.66%)`,
    };
  }
}
